import { LinearProgress, Stack } from "@mui/material";
import { Dialog } from "../../components/dialogs/Dialog";
import { RadioGroup } from "../../components/RadioGroup";
import {
  Allowed_Mime_Types,
  EmployeeStatusMap,
  EmployeeTrainingStatus,
  Error_File_Size,
  Error_Mime_Type,
  IntegrationSource,
  Max_File_Size,
  PAGE_SIZE_FOR_ADD_EDIT,
  Training_Dialog_Mode
} from "../../constants";

import { zodResolver } from "@hookform/resolvers/zod";
import { parseISO } from "date-fns";
import { ConfirmationDialog } from "klayowebshared";
import { isEmpty } from "lodash";
import { ChangeEvent, SyntheticEvent, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import axiosClient from "../../common/AxiosClient";
import { Utils } from "../../common/Utils";
import { DatePicker } from "../../components/DatePicker";
import { RequestActivityFeed } from "../../components/RequestActivityFeed";
import { CourseSelector } from "../../components/selectors/CourseSelector";
import { DocumentList } from "../../components/table/DocumentList";
import { TextField } from "../../components/TextField";
import { TrainingRequestActivity } from "../../data/attributes/TrainingRequestActivity";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppQuery } from "../../hooks/useAppQuery";
import { useAppSelector } from "../../hooks/useAppSelector";
import useDebounce from "../../hooks/useDebounce";
import useGetTrainingsWithPaging from "../../hooks/useGetTrainingsWithPaging";
import { IChecklistOption, ICheckLists } from "../../interfaces/CheckLists";
import { ChecklistAnswers } from "../../interfaces/Participants";
import { SELECTED_FILE_TYPE } from "../../interfaces/Practical";
import { IEmployeeTraining, TAddTrainingPayload } from "../../interfaces/Training";
import { useLocale } from "../../providers/LocaleProvider";
import { useSnackbar } from "../../providers/SnackbarProvider";
import {
  onCloseChecklistPreviewDialog,
  onOpenChecklistPreviewDialog
} from "../../store/checklist.slice";
import { AddTrainingSchema } from "../../utilities/validationSchema";

type Props = {
  theme: any;
  isOpen: boolean;
  onClose: () => void;
  onSave: (training: any) => void;
  employee: any;
  user: any;
  mode: { title: string; key: string };
  training?: any;
  integrations?: any[];
};

const defaultValues = {
  integrationCourse: null,
  state: EmployeeTrainingStatus.Approved.toString(),
  completionDate: undefined,
  commentText: "",
  formFiles: [],
  checklistAnswers: [],
  selectedFileType: SELECTED_FILE_TYPE.DOCUMENT,
  deletedDocumentIds: [],
  deletedChecklistAssignmentIds: []
};

const getEmployeeStatuses = (translate: (key: string) => string) =>
  Array.from(EmployeeStatusMap, ([key, value]) => ({
    label: translate(value),
    value: key
  }));

const AddEditTrainingDialog = ({
  theme,
  isOpen,
  onClose,
  employee,
  mode,
  training,
  user,
  onSave,
  integrations
}: Props) => {
  const { translate } = useLocale();
  const { showSnackbar } = useSnackbar();
  const [trainingSearch, setTrainingSearch] = useState("");
  const [showConfirmClose, setShowConfirmClose] = useState(false);

  const dispatch = useAppDispatch();
  const { checklistAnswers } = useAppSelector((state) => state.checklist);

  const {
    control,
    handleSubmit,
    reset,
    formState: { dirtyFields },
    watch,
    resetField,
    setValue
  } = useForm<TAddTrainingPayload>({
    resolver: zodResolver(AddTrainingSchema),
    defaultValues: defaultValues
  });

  const trainingSearchDebounce = useDebounce(trainingSearch, 500);

  const isChangedForm =
    !isEmpty(dirtyFields) || Object.values(dirtyFields).some((isDirty) => isDirty);

  const formValues = watch();
  const selectedProvider = watch().integration;
  const selectedFileType = watch("selectedFileType");
  const checklistAnswersForm = watch("checklistAnswers");
  const filesForm = watch("formFiles");
  const deletedDocumentIds = watch("deletedDocumentIds");
  const deletedChecklistAssignmentIds = watch("deletedChecklistAssignmentIds");

  // Completion date is only required when the training is marked as Approved.
  const isCompletionDateRequired =
    formValues.state === EmployeeTrainingStatus.Approved.toString();
  const completionDateLabel = translate(
    isCompletionDateRequired ? "training.completionDateRequired" : "training.completionDate"
  );

  const { isCreate } = useMemo(() => {
    return {
      isCreate: mode.key === Training_Dialog_Mode.add.key
    };
  }, [mode.key]);

  const { trainings, isLoading: isGetTrainingsLoading } = useGetTrainingsWithPaging({
    params: {
      pageNumber: 1,
      pageSize: PAGE_SIZE_FOR_ADD_EDIT,
      searchText: trainingSearchDebounce,
      integrationId: selectedProvider ? selectedProvider?.id.toString() : ""
    }
  });

  const { data: activity, isLoading: isGetActivityLoading } = useAppQuery<any>(
    `/EmployeeTraining/GetActivityHistory/${training?.id}`,
    {
      enabled: isOpen && !isCreate && !training?.isNew,
      select: (res) => new TrainingRequestActivity(res),
      placeholderData: undefined
    }
  );

  const trainingOptions = useMemo(() => {
    if (!trainings) return [];

    return trainings.map((training: any) => ({
      id: training.id,
      name: training.name
    }));
  }, [trainings]);

  const integrationOptions = useMemo(() => {
    if (!integrations) return [];

    return integrations
      .filter((integration) => integration.integrationSource === IntegrationSource.Manual)
      .map((integration) => ({
        id: integration.id,
        name: integration.name
      }));
  }, [integrations]);

  const documents = useMemo(() => {
    const documents = activity?.documents?.list || [];

    return documents.map((doc: any) => {
      if (deletedDocumentIds.includes(doc.documentId)) {
        return {
          ...doc,
          pendingDelete: true
        };
      }

      return doc;
    });
  }, [activity?.documents?.list, deletedDocumentIds]);

  const checklists = useMemo(() => {
    const checklists = training?.checklists || [];

    return checklists.filter((c: any) => !deletedChecklistAssignmentIds.includes(c.assignmentId));
  }, [training?.checklists, deletedChecklistAssignmentIds]);

  const onShowWarnCloseDialog = () => {
    if (isChangedForm) return setShowConfirmClose(true);

    onCloseDialog();
  };
  const onCloseDialog = () => {
    onClose();
    reset(defaultValues);
  };

  const onCancelClose = () => {
    setShowConfirmClose(false);
  };
  const onAcceptClose = () => {
    setShowConfirmClose(false);
    onCloseDialog();
  };

  const onSubmit = async (values: TAddTrainingPayload) => {
    onSave({
      ...training,
      organizationIntegrationIntegrationSource: IntegrationSource.Manual,
      integration: values.integration,
      completedDate: values.completionDate,
      name: values.integrationCourse?.name,
      integrationCourseId: values.integrationCourse?.id,
      status: EmployeeStatusMap.get(Number(values.state)),
      files: values.formFiles,
      hasDocuments:
        (values.formFiles &&
          Object.entries(values.formFiles).filter(([_, value]) => Boolean(value)).length > 0) ||
        !isEmpty(values.checklistAnswers),
      commentText: values.commentText,
      validationRequestState: Number(values.state),
      deletedDocumentIds: deletedDocumentIds,
      hasActions: true,
      checklistAnswers: values.checklistAnswers,
      deletedChecklistAssignmentIds: deletedChecklistAssignmentIds,
      selectedFileType: values.selectedFileType,
      ...(isCreate
        ? {
            isNew: true,
            id: (+new Date()).toString()
          }
        : {
            isNew: training?.isNew,
            id: training?.id
          })
    });

    onCloseDialog();
  };

  const onDocumentClick = async (e: SyntheticEvent, doc: any) => {
    try {
      const response = await axiosClient.get(`/EmployeeTraining/download/${doc.documentId}`, {
        responseType: "blob"
      });

      const blob = new Blob([response.data], { type: response.headers["content-type"] });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", doc.documentName);
      document.body.appendChild(link);
      link.click();

      showSnackbar(translate("practicals.downloadedDocument", { name: doc.documentName }), "info");
    } catch (error) {}
  };

  const onDeleteDocument = (doc: any) => {
    setValue("deletedDocumentIds", [...deletedDocumentIds, doc.documentId], {
      shouldDirty: true
    });
  };

  const onSearchTrainings = (value: string) => {
    setTrainingSearch(value);
  };

  const onTrainingsBlur = () => {
    if (!trainingSearch) return;
    setTrainingSearch("");
  };

  const isFormValid = () => {
    if (!isOpen || !isChangedForm) return false;

    const requiredFields = isCompletionDateRequired
      ? ["integrationCourse", "completionDate"]
      : ["integrationCourse"];

    const checklistAnswers = formValues.checklistAnswers;

    const isValidChecklists = checklistAnswers.every((checklist: any) => {
      const isComplete = Utils.checkAllAnswersComplete(checklist?.answers);

      return isComplete;
    });

    if (!isValidChecklists) return false;

    return requiredFields.every((field) => {
      const value = formValues[field as keyof typeof formValues];
      return !isEmpty(value);
    });
  };

  const onDeleteChecklist = (e: any, checklistId: string | number) => {
    const checklistAnswers = watch("checklistAnswers");

    setValue(
      "checklistAnswers",
      checklistAnswers.filter((c) => c.id !== checklistId),
      {
        shouldDirty: true
      }
    );
  };

  const onDeleteExistedChecklist = (e: any, checklistId: string) => {
    setValue("deletedChecklistAssignmentIds", [...deletedChecklistAssignmentIds, checklistId], {
      shouldDirty: true
    });
  };

  const onChecklistClick = (checklist: ICheckLists) => {
    dispatch(
      onOpenChecklistPreviewDialog({
        disabled: true,
        checklistData: checklist
      })
    );
  };

  const onSaveChecklistAnswers = (checklistAnswers: ChecklistAnswers) => {
    const checklistAnswersInForm = watch("checklistAnswers") || [];

    if (checklistAnswersInForm.find((c) => checklistAnswers.checklistId === c.id)) {
      setValue(
        "checklistAnswers",
        checklistAnswersInForm.map((c) => {
          if (checklistAnswers.checklistId === c.id) {
            return {
              ...c,
              answers: [...checklistAnswers.answers]
            };
          }

          return c;
        }),
        {
          shouldDirty: true
        }
      );
    }

    dispatch(onCloseChecklistPreviewDialog());
  };

  useEffect(() => {
    if (!training || !isOpen) return reset(defaultValues);

    const values = {
      ...defaultValues,
      integrationCourse: {
        id: training.integrationCourseId,
        name: training.name
      },
      completionDate: training.completedDate,
      state: String(training.validationRequestState || EmployeeTrainingStatus.Approved),
      formFiles: training.files || [],
      commentText: training.commentText || "",
      integration: training.integration,
      selectedFileType: training.selectedFileType || SELECTED_FILE_TYPE.DOCUMENT,
      checklistAnswers: training.checklistAnswers || [],
      deletedChecklistAssignmentIds: training.deletedChecklistAssignmentIds || [],
      deletedDocumentIds: training.deletedDocumentIds || []
    };

    reset(values);
  }, [training, reset, isOpen]);

  useEffect(() => {
    if (isEmpty(checklistAnswers)) return;

    onSaveChecklistAnswers(checklistAnswers);
  }, [checklistAnswers]);

  return (
    <>
      <Dialog
        open={isOpen}
        theme={theme}
        onClose={onShowWarnCloseDialog}
        fullWidth={true}
        title={mode.title}
        maxWidth='sm'
        actions={[
          {
            label: translate("common.save"),
            variant: "filled",
            type: "submit",
            form: "add-training-form",
            disabled: !isFormValid()
          },
          {
            label: translate("common.cancel"),
            variant: "outlined",
            onClick: onShowWarnCloseDialog,
            type: "button",
            form: "add-training-form"
          }
        ]}
      >
        {isGetActivityLoading ? (
          <LinearProgress />
        ) : (
          <form id='add-training-form' onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={5}>
              <Controller
                name='integration'
                control={control}
                render={({ field }) => {
                  return (
                    <CourseSelector
                      label={translate("training.provider")}
                      options={integrationOptions}
                      onChange={(e, integration: IEmployeeTraining) => {
                        field.onChange(integration);
                        resetField("integrationCourse");
                      }}
                      placeholder={translate("placeholders.selectProvider")}
                      value={field.value}
                      disabled={!isCreate}
                      translate={translate}
                    />
                  );
                }}
              />
              <Controller
                name='integrationCourse'
                control={control}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <CourseSelector
                      label={translate("training.trainingRequired")}
                      options={trainingOptions}
                      onChange={(e, course: IEmployeeTraining) => {
                        field.onChange(course);
                      }}
                      placeholder={translate("placeholders.selectTraining")}
                      value={field.value}
                      errorMessage={error?.message}
                      error={!!error?.message}
                      isLoading={isGetTrainingsLoading}
                      disabled={!isCreate || !selectedProvider}
                      onSearch={onSearchTrainings}
                      onBlur={onTrainingsBlur}
                      translate={translate}
                    />
                  );
                }}
              />

              <>
                <Controller
                  name='state'
                  control={control}
                  render={({ field }) => (
                    <RadioGroup
                      label={translate("training.trainingStatus")}
                      items={getEmployeeStatuses(translate)}
                      value={field.value}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        field.onChange(e.target.value);
                      }}
                    />
                  )}
                />

                <Controller
                  name='formFiles'
                  control={control}
                  render={({ field: formFilesField }) => (
                    <Controller
                      name='checklistAnswers'
                      control={control}
                      render={({ field: checklistField }) => {
                        return (
                          <DocumentList
                            theme={theme}
                            label={translate("labels.evidence")}
                            files={filesForm}
                            onFilesChange={(e, files) => {
                              formFilesField.onChange(Object.values(files));
                            }}
                            allowedMimeTypes={Allowed_Mime_Types}
                            maxFileSize={Max_File_Size}
                            errorMimeType={Error_Mime_Type}
                            errorFileSize={Error_File_Size}
                            documents={documents}
                            onViewDocument={onDocumentClick}
                            onDeleteDocument={(e: any, doc: any) => {
                              onDeleteDocument(doc);
                            }}
                            onChange={(_: any, value: IChecklistOption[]) => {
                              const checklistAnswers = value.map((c) => {
                                const existedChecklistAnswer = checklistAnswersForm.find(
                                  (existed: any) => existed.id === c.id
                                );

                                if (existedChecklistAnswer) {
                                  return {
                                    ...c,
                                    answers: existedChecklistAnswer?.answers || []
                                  };
                                }

                                return c;
                              });

                              checklistField.onChange(checklistAnswers);
                            }}
                            checklistsUploaded={checklists.filter((c: any) => !c.assignmentId)}
                            selectedChecklist={checklistAnswersForm as any}
                            checklists={checklists}
                            fileType={selectedFileType}
                            setSelectedFileType={(type: SELECTED_FILE_TYPE) => {
                              setValue("selectedFileType", type);
                            }}
                            onDeleteChecklist={onDeleteChecklist}
                            onDeleteExistedChecklist={onDeleteExistedChecklist}
                            isRequireComplete
                            translate={translate}
                          />
                        );
                      }}
                    />
                  )}
                />

                <Controller
                  name='completionDate'
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <DatePicker
                      theme={theme}
                      label={completionDateLabel}
                      onChange={(date, valid) => {
                        // Allow manual typing; only persist a valid date. An invalid/partial
                        // typed date (valid === false) clears the form value so Utils.toApiDate
                        // never runs toISOString() on an Invalid Date (which would throw).
                        field.onChange(valid ? Utils.toApiDate(date) : null);
                      }}
                      value={field.value ? new Date(parseISO(field.value)) : undefined}
                      allowPast={true}
                      allowEmpty={true}
                      textFieldProps={{ fullWidth: true }}
                      error={!!error?.message}
                      errorMessage={error?.message}
                    />
                  )}
                />

                <Controller
                  name='commentText'
                  control={control}
                  render={({ field }) => (
                    <TextField
                      value={field.value}
                      label={translate("labels.comments")}
                      placeholder={translate("training.addCommentsPlaceholder")}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                      }}
                    />
                  )}
                />
              </>

              {!isCreate && activity && (
                <RequestActivityFeed
                  user={user}
                  employee={employee}
                  activity={activity}
                  onDocumentClick={onDocumentClick}
                  onChecklistClick={onChecklistClick}
                  theme={theme}
                />
              )}
            </Stack>
          </form>
        )}
        {showConfirmClose && (
          <ConfirmationDialog
            title={translate("dialogs.unsavedChanges")}
            theme={theme}
            question={translate("training.unsavedChangesQuestion")}
            cancelButton={translate("common.cancel")}
            acceptButton={translate("training.leavePage")}
            onCancel={onCancelClose}
            onAccept={onAcceptClose}
          />
        )}
      </Dialog>
    </>
  );
};

export default AddEditTrainingDialog;
